// The window: Home beside the list when wide, folded away when medium, two screens when narrow (docs/47, decision 1);
// the floating Find and New, the selection bar, the toast and the sheets; and the keyboard as on the Mac
// (MacShortcutsView.swift), with keys the browser leaves free (design decision 3).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Route } from "../core/lists";
import { t } from "../i18n";
import { WindowContext, useMedia, type Composing, type HabitWeekToast, type Sheet, type UI, type Window as WindowValue } from "./context";
import { Icon } from "./Icon";
import { ListScreen, listSections } from "./ListScreen";
import { setPrefs } from "./prefs";
import type { Model, ModelState } from "./model";
import { SelectionBar } from "./SelectionBar";
import { Sheets } from "./Sheets";
import { Sidebar } from "./Sidebar";
import { UpdateNote } from "./Update";
import { setWeekPref, usePrefs } from "./prefs";
import { habitOfRow, habitRowIDs, habitTicked, HabitWeekToastView, openNewHabit, tickAskingFirst, tickSound, weekToastStill } from "./Habits";
import { movedByDays, paged, WEEK_DAYS } from "../core/week";
import { applyTagFilter } from "../core/tag-filter";
import { columnOf, weekBase, weekView } from "./Week";

const GO_KEYS: Record<string, Route> = {
  t: { kind: "today" }, i: { kind: "inbox" }, h: { kind: "habits" }, d: { kind: "due" }, s: { kind: "scheduled" },
  a: { kind: "anytime" }, o: { kind: "someday" }, r: { kind: "archive" },
};

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || target.matches("input, textarea, select");
}

export function Window({ model, state }: { model: Model; state: ModelState }) {
  const narrow = useMedia("(max-width: 699px)");
  const mediumOrLess = useMedia("(max-width: 999px)");
  const touch = useMedia("(pointer: coarse)");
  const layout = useMemo(() => ({ narrow, medium: mediumOrLess && !narrow, touch }), [narrow, mediumOrLess, touch]);

  const [ui, setUI] = useState<UI>(() => ({
    route: { kind: "today" }, screen: typeof matchMedia === "function" && matchMedia("(max-width: 699px)").matches ? "home" : "list",
    selected: [], open: null, composing: null, finding: false, query: "", sheet: null, toast: null, sidebarOpen: false, sidebarHidden: false,
    archiveFilter: null, weekStart: null, weekFilter: "", weekExpanded: [], habitPage: null, habitsFilter: "today",
    todayTags: [],
  }));
  const prefs = usePrefs();
  const anchor = useRef<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const set = useCallback((change: Partial<UI> | ((ui: UI) => Partial<UI>)) => {
    setUI((current) => ({ ...current, ...(typeof change === "function" ? change(current) : change) }));
  }, []);

  // The open card stays where it was while it is open, even when a change takes it out of the list (a new day, a
  // move); it leaves when it closes.
  const openPlace = useRef<{ id: string; section: string; index: number } | null>(null);
  const searching = ui.finding && ui.query.trim() !== "";
  const showsWeek = ui.route.kind === "scheduled" && prefs.week.shows && !searching;
  // Today as a list (not as a day, useShowsDay) with tags picked: the rows keep those with any of them.
  const narrowsByTags = ui.route.kind === "today" && !searching && ui.todayTags.length > 0 && !(prefs.todayShowsDay && state.isPro);
  const sections = useMemo(() => {
    // The week shows its own columns: Scheduled's list isn't worked out under it, also not when a to-do opens.
    if (showsWeek) return [];
    const all = listSections(searching ? { kind: "search", query: ui.query } : ui.route, state.lists, prefs);
    // Today's list narrowed to the tags picked above it (not the Day view, not Find).
    const shown = narrowsByTags ? applyTagFilter(new Set(ui.todayTags), all) : all;
    if (ui.open === null) return shown;
    const found = shown.find((section) => section.todos.some((todo) => todo.id === ui.open));
    if (found) {
      openPlace.current = { id: ui.open, section: found.id, index: found.todos.findIndex((todo) => todo.id === ui.open) };
      return shown;
    }
    const place = openPlace.current;
    const todo = state.records.todos.find((other) => other.id === ui.open);
    if (!place || place.id !== ui.open || !todo) return shown;
    return shown.map((section) => {
      if (section.id !== place.section) return section;
      const todos = [...section.todos];
      todos.splice(Math.min(place.index, todos.length), 0, todo);
      return { ...section, todos };
    });
  }, [showsWeek, searching, narrowsByTags, ui.todayTags, ui.route, ui.query, ui.open, state.lists, state.records.todos, prefs]);
  // Scheduled as a week: worked out when the records or the week's own state change, not on every key (docs/56).
  const { weekStart, weekFilter, weekExpanded, open } = ui;
  const { band: weekTab, showsBand: weekShowsBand } = prefs.week;
  // The days, the tab and its counts apart from what opening a to-do changes, so an open doesn't work them out again.
  const base = useMemo(() => (showsWeek ? weekBase(state.lists, weekStart, weekFilter, weekTab, weekShowsBand) : null),
    [showsWeek, state.lists, weekStart, weekFilter, weekTab, weekShowsBand]);
  const week = useMemo(() => (base ? weekView(base, { weekExpanded, open }) : null), [base, weekExpanded, open]);
  const collapsedSections = useMemo(() => new Set(state.records.sections.filter((s) => s.isCollapsed).map((s) => s.id)), [state.records.sections]);
  const listIDs = useMemo(() => sections.flatMap((section) => (section.header.kind === "section" && collapsedSections.has(section.header.section.id) ? [] : section.todos.map((todo) => todo.id))),
    [sections, collapsedSections]);
  // Habits' rows, and an area's habits at the top of its page, are rows as well (Habits.tsx).
  const { route, habitsFilter, finding, query } = ui;
  const habitIDs = useMemo(() => habitRowIDs({ route, habitsFilter, finding, query }, state, model),
    [route, habitsFilter, finding, query, state, model]);
  const visibleIDs = useMemo(() => (week ? week.columns.flatMap((column) => column.ids) : [...habitIDs, ...listIDs]), [week, habitIDs, listIDs]);

  const showToast = useCallback((text: string, undo = false, habit?: HabitWeekToast) => {
    clearTimeout(toastTimer.current);
    set({ toast: { id: Date.now(), text, undo, ...(habit ? { habit } : {}) } });
    // The week's moment takes a second: the toast stays its usual time after it (boards 9 and 10).
    const stays = habit?.tick.closes ? 5200 : 4000;
    if (!weekToastStill()) toastTimer.current = setTimeout(() => set({ toast: null }), stays);
  }, [set]);

  const go = useCallback((route: Route) => {
    anchor.current = null;
    set((current) => {
      // The Archive's filter is forgotten once the list is left.
      if (current.archiveFilter && route.kind !== "archive") model.setArchiveFilter(undefined);
      // A section just made shows while empty only until Today is left.
      if (current.route.kind === "today" && route.kind !== "today") model.leftToday();
      return {
        route, selected: [], open: null, composing: null, finding: false, query: "", screen: "list", sidebarOpen: false,
        archiveFilter: route.kind === "archive" ? current.archiveFilter : null, weekStart: null, weekFilter: "", weekExpanded: [],
        habitPage: null, habitsFilter: "today",
      };
    });
    document.querySelector(".main")?.scrollTo({ top: 0 });
  }, [set, model]);

  const select = useCallback((id: string, how: { toggle?: boolean; range?: boolean } = {}) => {
    set((current) => {
      if (how.toggle) {
        anchor.current = id;
        return { selected: current.selected.includes(id) ? current.selected.filter((other) => other !== id) : [...current.selected, id], open: null };
      }
      if (how.range && anchor.current !== null) {
        const a = visibleIDs.indexOf(anchor.current);
        const b = visibleIDs.indexOf(id);
        if (a >= 0 && b >= 0) return { selected: visibleIDs.slice(Math.min(a, b), Math.max(a, b) + 1), open: null };
      }
      anchor.current = id;
      return { selected: [id], open: null };
    });
  }, [set, visibleIDs]);

  const openTodo = useCallback((id: string | null) => set({ open: id, selected: id ? [id] : [], composing: null }), [set]);
  const compose = useCallback((composing: Composing = {}) => {
    set({ composing, open: null, selected: [], finding: false, query: "", screen: "list" });
  }, [set]);
  const showSheet = useCallback((sheet: Sheet | null) => set({ sheet }), [set]);

  // The selection keeps only what is still in the list.
  useEffect(() => {
    if (ui.selected.some((id) => !visibleIDs.includes(id))) set({ selected: ui.selected.filter((id) => visibleIDs.includes(id)) });
    if (ui.open !== null && !visibleIDs.includes(ui.open) && !state.records.todos.some((todo) => todo.id === ui.open)) set({ open: null });
  }, [visibleIDs, ui.selected, ui.open, state.records.todos, set]);

  // Back at a width where Home and the list sit side by side, the list shows.
  useEffect(() => {
    if (!narrow && ui.screen === "home") set({ screen: "list" });
    if (!layout.medium && ui.sidebarOpen) set({ sidebarOpen: false });
  }, [narrow, layout.medium, ui.screen, ui.sidebarOpen, set]);

  const window: WindowValue = { model, state, ui, set, go, select, openTodo, compose, showToast, showSheet, visibleIDs, layout, week };

  const toggleHome = useCallback(() => {
    if (narrow) set({ screen: ui.screen === "home" ? "list" : "home", open: null, composing: null, selected: [] });
    else if (layout.medium) set({ sidebarOpen: !ui.sidebarOpen });
    else set({ sidebarHidden: !ui.sidebarHidden });
  }, [narrow, layout.medium, ui.screen, ui.sidebarOpen, ui.sidebarHidden, set]);

  // MARK: Keys
  const pendingG = useRef<ReturnType<typeof setTimeout> | null>(null);
  const keyHandler = useRef<(event: KeyboardEvent) => void>(() => {});
  keyHandler.current = (event: KeyboardEvent) => {
    if (event.defaultPrevented || ui.sheet) return;
    // A habit's page has its own keys (← and Esc back, Space for today); the list's wait (Habits.tsx: HabitPage).
    if (ui.habitPage !== null) return;
    const command = event.metaKey || event.ctrlKey;
    const typing = isTyping(event.target);
    if (event.key === "Escape") {
      if (ui.open !== null || ui.composing !== null) return; // The card closes itself.
      if (ui.sidebarOpen) return set({ sidebarOpen: false });
      if (ui.finding) return set({ finding: false, query: "" });
      if (ui.selected.length) return set({ selected: [] });
      return;
    }
    if (typing) return;
    // While a to-do or Quick Entry is open, the card has the keys (docs/55, item 14): Return and Space act on the
    // focused chip, tool or menu item, ↑↓ stay in the card, letters stay in the pickers.
    if (ui.open !== null || ui.composing !== null) return;
    // Inside a popover or a sheet the keys are its own; on a button, Return and Space press it.
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target?.closest(".popover, .sheet-backdrop")) return;
    const onControl = target?.matches("button, a, select, [role=menuitem], [role=menuitemcheckbox]") ?? false;
    if (onControl && (event.key === "Enter" || event.key === " ")) return;
    if (command && event.key.toLowerCase() === "z") {
      event.preventDefault();
      if (event.shiftKey) {
        if (state.undo.canRedo) { model.redo(); showToast(t("Redone")); }
      } else if (state.undo.canUndo) { model.undo(); showToast(t("Undone")); }
      return;
    }
    if (command && event.key.toLowerCase() === "y" && !event.metaKey) {
      event.preventDefault();
      if (state.undo.canRedo) { model.redo(); showToast(t("Redone")); }
      return;
    }
    if (command && event.key.toLowerCase() === "a" && visibleIDs.length) {
      event.preventDefault();
      return set({ selected: visibleIDs, open: null });
    }
    // ⌥→ / ⌥← a day later or earlier, in every list (docs/56; the Mac's ⌥⌘→ / ⌥⌘←).
    if (event.altKey && !command && (event.key === "ArrowRight" || event.key === "ArrowLeft") && ui.selected.length) {
      event.preventDefault();
      const later = event.key === "ArrowRight";
      const changed = movedByDays(state.records.todos.filter((todo) => ui.selected.includes(todo.id)), later ? 1 : -1, state.today, state.zone);
      if (changed.length) {
        model.updateMany(changed, later ? "A Day Later" : "A Day Earlier");
        showToast(later ? t("A Day Later") : t("A Day Earlier"), true);
      }
      return;
    }
    if (command || event.altKey) return;
    if (week && weekKeys(event)) return;
    if (pendingG.current) {
      clearTimeout(pendingG.current);
      pendingG.current = null;
      const route = GO_KEYS[event.key.toLowerCase()];
      if (route) { event.preventDefault(); return go(route); }
    }
    const current = ui.selected.at(-1);
    switch (event.key) {
      case "y": case "Y":
        // Scheduled as a week, or back to its list (the Mac's ⌥⌘Y).
        event.preventDefault();
        if (ui.route.kind !== "scheduled") { setWeekPref({ shows: true }); return go({ kind: "scheduled" }); }
        setWeekPref({ shows: !prefs.week.shows });
        return set({ selected: [] });
      case "q": case "Q": event.preventDefault(); return ui.route.kind === "habits" ? openNewHabit(window) : compose();
      case "ArrowRight": {
        // → on a habit's day opens its page, as on the Mac (docs/63, board 21).
        const habitID = habitOfRow(state, current);
        if (current === undefined || habitID === undefined || event.shiftKey) return;
        event.preventDefault();
        return set({ habitPage: habitID, selected: [], open: null });
      }
      case "f": case "F": case "/": event.preventDefault(); return set({ finding: true, open: null, composing: null, screen: "list" });
      case "g": case "G": pendingG.current = setTimeout(() => { pendingG.current = null; }, 1500); return;
      case "?": event.preventDefault(); return showSheet({ kind: "keys" });
      case "\\": event.preventDefault(); return toggleHome();
      case "o": case "O": event.preventDefault(); return setPrefs((current) => ({ ...current, showsOverview: !current.showsOverview }));
      case "ArrowDown": case "ArrowUp": {
        if (!visibleIDs.length || (narrow && ui.screen === "home")) return;
        event.preventDefault();
        const index = current === undefined ? -1 : visibleIDs.indexOf(current);
        const step = event.key === "ArrowDown" ? 1 : -1;
        const nextIndex = index < 0 ? (step > 0 ? 0 : visibleIDs.length - 1) : Math.max(0, Math.min(visibleIDs.length - 1, index + step));
        const next = visibleIDs[nextIndex];
        if (next === undefined) return;
        if (event.shiftKey) {
          // Back over the selection shrinks it; on beyond it grows (the last chosen stays the cursor).
          if (ui.selected.includes(next) && next !== current) set({ selected: [...ui.selected.filter((id) => id !== current && id !== next), next] });
          else set({ selected: [...ui.selected.filter((id) => id !== next), next] });
        }
        else { anchor.current = next; set({ selected: [next] }); }
        requestAnimationFrame(() => {
          const row = document.querySelector<HTMLElement>(`[data-row="${next}"]`);
          row?.scrollIntoView({ block: "nearest" });
          row?.focus({ preventScroll: true });
        });
        return;
      }
      case " ":
        if (ui.selected.length) {
          event.preventDefault();
          completeWithToast(ui.selected);
        }
        return;
      case "Enter":
        if (current !== undefined) { event.preventDefault(); openTodo(current); }
        return;
      case "Backspace": case "Delete":
        if (ui.selected.length) {
          event.preventDefault();
          const count = ui.selected.length;
          model.delete(ui.selected);
          set({ selected: [] });
          showToast(count > 1 ? t("{count} to-dos deleted", { count }) : t("To-do deleted"), true);
        }
        return;
    }
  };
  /** The week's own keys: ← → between columns, ↑ ↓ inside one, ⇧[ ⇧] a week, 0 this week, P the band, Tab into it, Q on a day. */
  function weekKeys(event: KeyboardEvent): boolean {
    if (!week) return false;
    const current = ui.selected.at(-1);
    const at = columnOf(week, current);
    const focus = (id: string) => {
      anchor.current = id;
      set({ selected: [id] });
      requestAnimationFrame(() => {
        const row = document.querySelector<HTMLElement>(`[data-row="${id}"]`);
        row?.scrollIntoView({ block: "nearest", inline: "nearest" });
        row?.focus({ preventScroll: true });
      });
    };
    const start = week.start;
    const pageBy = (days: number) => {
      const next = paged(start, days, state.today);
      set({ weekStart: next === state.today ? null : next, selected: [] });
    };
    switch (event.key) {
      case "ArrowLeft": case "ArrowRight": {
        event.preventDefault();
        const step = event.key === "ArrowRight" ? 1 : -1;
        const row = at < 0 ? 0 : Math.max(0, week.columns[at]!.ids.indexOf(current!));
        // To the next column that has rows, at the same height or its last row.
        for (let index = at < 0 ? (step > 0 ? 0 : week.columns.length - 1) : at + step; index >= 0 && index < week.columns.length; index += step) {
          const ids = week.columns[index]!.ids;
          if (ids.length) { focus(ids[Math.min(row, ids.length - 1)]!); break; }
        }
        return true;
      }
      case "ArrowUp": case "ArrowDown": {
        if (event.shiftKey) return false;
        event.preventDefault();
        if (at < 0) {
          const first = week.columns.find((column) => column.ids.length)?.ids[0];
          if (first) focus(first);
          return true;
        }
        const ids = week.columns[at]!.ids;
        const index = ids.indexOf(current!) + (event.key === "ArrowDown" ? 1 : -1);
        const next = ids[Math.max(0, Math.min(ids.length - 1, index))];
        if (next) focus(next);
        return true;
      }
      case "Tab":
        if (event.shiftKey || !week.showsBand) return false;
        event.preventDefault();
        document.dispatchEvent(new CustomEvent("todolix:week-band"));
        return true;
      case "0": event.preventDefault(); set({ weekStart: null, selected: [] }); return true;
      case "p": case "P": event.preventDefault(); setWeekPref({ showsBand: !week.showsBand }); return true;
      case "q": case "Q": {
        // A new to-do on the day of the chosen column (today's when none is chosen).
        const day = at >= 0 ? week.columns[at]!.day : undefined;
        if (at >= 0 && day === undefined) return false;
        event.preventDefault();
        compose({ day: day ?? week.days[0]!.day });
        return true;
      }
    }
    if (event.shiftKey && (event.code === "BracketLeft" || event.code === "BracketRight" || event.key === "{" || event.key === "}")) {
      event.preventDefault();
      pageBy(event.code === "BracketRight" || event.key === "}" ? WEEK_DAYS : -WEEK_DAYS);
      return true;
    }
    return false;
  }

  useEffect(() => {
    const listener = (event: KeyboardEvent) => keyHandler.current(event);
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, []);

  function completeWithToast(ids: readonly string[]) {
    const todos = state.records.todos.filter((todo) => ids.includes(todo.id));
    const completing = todos.some((todo) => todo.completedAt === undefined);
    // The soft sound, once for the tick, however many it completes (docs/59); the habits' own when one is a habit's day.
    const ticked = completing ? todos.filter((todo) => todo.completedAt === undefined) : [];
    // The morning after a missed day one habit's day asks first (docs/70).
    tickAskingFirst(window, ticked, () => {
      if (completing) tickSound(ticked);
      model.toggleCompletion(ids);
      set({ selected: [] });
      if (completing) showToast(ids.length > 1 ? t("{count} completed", { count: ids.length }) : t("Completed"), true);
      if (completing) habitTicked(window, ticked);
    });
  }

  const hideFabs = ui.selected.length > 1 && ui.open === null;
  const className = ["app", ui.sidebarOpen ? "sidebar-open" : "", ui.sidebarHidden ? "sidebar-hidden" : ""].filter(Boolean).join(" ");

  return (
    <WindowContext.Provider value={window}>
      <div className={className} data-screen={ui.screen}>
        <Sidebar />
        <div className="scrim" onClick={() => set({ sidebarOpen: false })} aria-hidden="true" />
        <ListScreen sections={sections} onToggleHome={toggleHome} onComplete={completeWithToast} />
      </div>
      {!hideFabs && (
        <div className="fab-group">
          <button className="glass fab-find" aria-label={t("Find")} title={`${t("Find")} (F)`} onClick={() => set({ finding: true, open: null, composing: null, screen: "list" })}>
            <Icon name="search" />
          </button>
          <button className="fab-new" aria-label={ui.route.kind === "habits" ? t("New Habit") : t("New To-Do")}
            title={`${ui.route.kind === "habits" ? t("New Habit") : t("New To-Do")} (Q)`}
            onClick={() => (ui.route.kind === "habits" ? openNewHabit(window) : compose())}>
            <Icon name="plus" />
          </button>
        </div>
      )}
      <SelectionBar onComplete={completeWithToast} />
      {ui.toast && (
        <div className={`toast${ui.toast.habit ? " habit-toast" : ""}`} role="status" key={ui.toast.id}>
          {ui.toast.habit ? <HabitWeekToastView note={ui.toast.habit} /> : <span>{ui.toast.text}</span>}
          {ui.toast.undo && state.undo.canUndo && (
            <button onClick={() => { model.undo(); set({ toast: null }); }}>{t("Undo")}</button>
          )}
        </div>
      )}
      <UpdateNote />
      <Sheets />
    </WindowContext.Provider>
  );
}
